import { Branch } from 'api/branchApi';
import { Client } from 'api/clientsApi';
import {
  ConsumableBuyAndSellRateType,
  ConsumableItem,
  ConsumableRateFormType,
} from 'api/consumablesApi';
import {
  EquipmentBuyAndSellRateType,
  EquipmentCategoryItem,
  EquipmentRateFormType,
} from 'api/equipmentApi';
import {
  LabourBillingRate,
  LabourBillingRateFormType,
  LabourCostRate,
  LabourCostRateFormType,
  LabourCostRateUserOverride,
  LabourCostRateUserOverrideFormType,
} from 'api/labourApi';
import { currencyType } from 'api/organizationApi';
import {
  OtherExpenseRateFormType,
  OtherExpensesMargin,
} from 'api/otherExpensesApi';

// Shared by the Consumable and Labour scoped rate-import dialogs to describe a Branch/Client scope
export const comboParts = (branch: Branch | null, client: Client | null) => {
  const parts: { label: string; value: string }[] = [];
  if (branch) parts.push({
    label: 'Branch', value: `"${branch.name}"` });
  if (client) parts.push({ label: 'Client', value: `"${client.legalName}"` });
  return parts;
};

export const comboLabel = (branch: Branch | null, client: Client | null): string =>
  comboParts(branch, client)
    .map((p) => `${p.label} - ${p.value}`)
    .join(' and ');

export type RateKind = 'buy' | 'sell';

// Shared by the Consumable and Equipment scoped rate-import forms/tabs 
export const isComboReady = (
  rateKind: RateKind,
  branch: Branch | null,
  client: Client | null
): boolean => (rateKind === 'buy' ? !!branch : !!branch || !!client);

interface FilterConsumableParams {
  consumableItemId?: string;
  country?: string;
  state?: string;
  branchId?: string;
  clientId?: string;
}

interface FilterEquipmentParams {
  equipmentCategoryId?: string;
  country?: string;
  state?: string;
  branchId?: string;
  clientId?: string;
}

export interface ConsumableFilterStateParams {
  filterSectionOpen: boolean;
  branchIds: Branch[];
  itemNames: ConsumableItem[];
  filterClient: Client[];
  unitType: string[];
}

export interface EquipmentCategoryFilterStateParams {
  filterSectionOpen: boolean;
  branchIds: Branch[];
  itemNames: EquipmentCategoryItem[];
  filterClient: Client[];
}

export function filterConsumableRates(
  consumableList: { data?: ConsumableItem[] },
  params: FilterConsumableParams,
  rateType: 'sellRates' | 'buyRates'
): ConsumableItem | {} {
  const { consumableItemId, country, state, branchId, clientId } = params;

  const filteredItem = consumableList.data?.find(
    (item) => item?.id === consumableItemId
  );

  if (!filteredItem) return {};
  const rates = filteredItem[rateType];
  let filteredRates = rates || [];

  if (country) {
    const branchRates = rates?.filter(
      (rate) => rate?.branch?.country === country
    );
    const clientRates = rates?.filter((rate) =>
      rate?.client?.address.includes(country)
    );

    const uniqueRates = new Map();
    [...(branchRates || []), ...(clientRates || [])].forEach((rate) => {
      uniqueRates.set(rate.id, rate);
    });

    filteredRates = Array.from(uniqueRates.values());
  }

  if (state) {
    filteredRates = filteredRates.filter((rate) => {
      const branchMatch = rate?.branch?.state === state;
      const clientMatch = rate?.client?.address.includes(state);
      return branchMatch || clientMatch;
    });
  }

  if (branchId) {
    filteredRates = filteredRates.filter((rate) => rate.branchId === branchId);
  }

  if (clientId) {
    filteredRates = filteredRates.filter(
      (rate) => rate?.client?.id === clientId
    );
  }

  return {
    ...filteredItem,
    [rateType]: filteredRates.length > 0 ? filteredRates : [],
  };
}

export function filterEquipmentRates(
  equipmentList: { data?: EquipmentCategoryItem[] },
  params: FilterEquipmentParams,
  rateType: 'sellRates' | 'buyRates'
): EquipmentCategoryItem | {} {
  const { equipmentCategoryId, country, state, branchId, clientId } = params;

  const filteredItem = equipmentList.data?.find(
    (item) => item?.id === equipmentCategoryId
  );

  if (!filteredItem) return {};
  const rates = filteredItem[rateType];
  let filteredRates = rates || [];

  if (country) {
    const branchRates = rates?.filter(
      (rate) => rate?.branch?.country === country
    );
    const clientRates = rates?.filter((rate) =>
      rate?.client?.address.includes(country)
    );

    const uniqueRates = new Map();
    [...(branchRates || []), ...(clientRates || [])].forEach((rate) => {
      uniqueRates.set(rate.id, rate);
    });

    filteredRates = Array.from(uniqueRates.values());
  }

  if (state) {
    filteredRates = filteredRates.filter((rate) => {
      const branchMatch = rate?.branch?.state === state;
      const clientMatch = rate?.client?.address.includes(state);
      return branchMatch || clientMatch;
    });
  }

  if (branchId) {
    filteredRates = filteredRates.filter((rate) => rate.branchId === branchId);
  }

  if (clientId) {
    filteredRates = filteredRates.filter(
      (rate) => rate?.client?.id === clientId
    );
  }

  return {
    ...filteredItem,
    [rateType]: filteredRates.length > 0 ? filteredRates : [],
  };
}

export const filterConsumableData = (
  consumableData: ConsumableItem[],
  filterState: ConsumableFilterStateParams
) => {
  if (!consumableData) {
    return [];
  }
  let filtered = [...consumableData];

  if (filterState.branchIds.length > 0) {
    const branchIdSet = new Set(filterState.branchIds.map((b) => b.id));

    filtered = filtered
      .filter(
        (item) =>
          item.buyRates?.some((rate) => branchIdSet.has(rate.branchId)) ||
          item.sellRates?.some((rate) => branchIdSet.has(rate.branchId))
      )
      .map((item) => ({
        ...item,
        buyRates: item.buyRates?.filter((rate) =>
          branchIdSet.has(rate.branchId)
        ),
        sellRates: item.sellRates?.filter((rate) =>
          branchIdSet.has(rate.branchId)
        ),
      }));
  }

  if (filterState.filterClient.length > 0) {
    const filterClientSet = new Set(filterState.filterClient.map((b) => b.id));
    filtered = filtered
      .filter(
        (item) =>
          item.buyRates?.some((rate) => filterClientSet.has(rate.clientId)) ||
          item.sellRates?.some((rate) => filterClientSet.has(rate.clientId))
      )
      .map((item) => ({
        ...item,
        buyRates: item.buyRates?.filter((rate) =>
          filterClientSet.has(rate.clientId)
        ),
        sellRates: item.sellRates?.filter((rate) =>
          filterClientSet.has(rate.clientId)
        ),
      }));
  }

  if (filterState.itemNames.length > 0) {
    const nameSet = new Set(
      filterState.itemNames.map((item) =>
        typeof item === 'string' ? item : item.name
      )
    );

    filtered = filtered.filter((item) => nameSet.has(item.name));
  }

  if (filterState.unitType.length > 0) {
    const typeSet = new Set(
      filterState.unitType.map((item) =>
        typeof item === 'string' ? item : item
      )
    );

    filtered = filtered.filter((item) => {
      const matchesType = typeSet.has(item.unitType);
      return matchesType;
    });
  }
  return filtered;
};

export const filterEquipmentData = (
  equipmentData: EquipmentCategoryItem[],
  filterState: EquipmentCategoryFilterStateParams
) => {
  if (!equipmentData) {
    return [];
  }

  let filtered = [...equipmentData];
  if (filterState.branchIds.length > 0) {
    const branchIdSet = new Set(filterState.branchIds.map((b) => b.id));
    filtered = filtered
      .filter(
        (item) =>
          item.buyRates?.some((rate) => branchIdSet.has(rate.branchId)) ||
          item.sellRates?.some((rate) => branchIdSet.has(rate.branchId))
      )
      .map((item) => ({
        ...item,
        buyRates: item.buyRates?.filter((rate) =>
          branchIdSet.has(rate.branchId)
        ),
        sellRates: item.sellRates?.filter((rate) =>
          branchIdSet.has(rate.branchId)
        ),
      }));
  }

  if (filterState.filterClient.length > 0) {
    const filterClientSet = new Set(filterState.filterClient.map((b) => b.id));
    filtered = filtered
      .filter(
        (item) =>
          item.buyRates?.some((rate) => filterClientSet.has(rate.clientId)) ||
          item.sellRates?.some((rate) => filterClientSet.has(rate.clientId))
      )
      .map((item) => ({
        ...item,
        buyRates: item.buyRates?.filter((rate) =>
          filterClientSet.has(rate.clientId)
        ),
        sellRates: item.sellRates?.filter((rate) =>
          filterClientSet.has(rate.clientId)
        ),
      }));
  }

  if (filterState.itemNames.length > 0) {
    const nameSet = new Set(
      filterState.itemNames.map((item) =>
        typeof item === 'string' ? item : item.name
      )
    );

    filtered = filtered.filter((item) => {
      const matchesType = nameSet.has(item.name);
      return matchesType;
    });
  }

  return filtered;
};

export const getConsumableBuyBranchKey = (
  r: ConsumableBuyAndSellRateType
): string => {
  if (r.branch?.name) return r.branch.name;
  return 'Default Price';
};

export const getEquipmentBuyBranchKey = (
  r: EquipmentBuyAndSellRateType
): string => {
  if (r.branch?.name) return r.branch.name;
  return 'Default Price';
};

export const getConsumableSellBranchKey = (
  r: ConsumableBuyAndSellRateType
): string => {
  if (r.branch?.name && r.client?.legalName)
    return `${r.branch.name}/${r.client.legalName}`;
  if (r.client?.legalName) return r.client.legalName;
  if (r.branch?.name) return r.branch.name;
  if (!r.branch?.name && !r.client?.legalName) return 'Default Price';
  return null;
};

export const getEquipmentSellBranchKey = (
  r: EquipmentBuyAndSellRateType
): string => {
  if (r.branch?.name && r.client?.legalName)
    return `${r.branch.name}/${r.client.legalName}`;
  if (r.client?.legalName) return r.client.legalName;
  if (r.branch?.name) return r.branch.name;
  if (!r.branch?.name && !r.client?.legalName) return 'Default Price';
  return null;
};

export const groupedConsumableBuyItems = (item: ConsumableItem) =>
  item?.buyRates
    ? Object.entries(
        item.buyRates.reduce(
          (
            acc: Record<string, ConsumableBuyAndSellRateType[]>,
            rate: ConsumableBuyAndSellRateType
          ) => {
            let key = '';
            if (rate?.branch && rate?.client) {
              key = `${rate.branch.name} / ${rate.client.legalName}`;
            } else if (rate?.branch) {
              key = rate.branch.name;
            } else if (rate?.client) {
              key = rate.client.legalName;
            } else {
              key = 'Default Price';
            }

            if (!acc[key]) {
              acc[key] = [];
            }
            acc[key].push(rate);
            return acc;
          },
          {} as Record<string, ConsumableBuyAndSellRateType[]>
        )
      ).map(([key, rates]) => ({
        key,
        rates,
      }))
    : [];

export const groupedConsumableSellItems = (item: ConsumableItem) =>
  item?.sellRates
    ? Object.entries(
        item.sellRates.reduce(
          (
            acc: Record<string, ConsumableBuyAndSellRateType[]>,
            rate: ConsumableBuyAndSellRateType
          ) => {
            let key = '';
            if (rate?.branch && rate?.client) {
              key = `${rate.branch.name} / ${rate.client.legalName}`;
            } else if (rate?.branch) {
              key = rate.branch.name;
            } else if (rate?.client) {
              key = rate.client.legalName;
            } else {
              key = 'Default Price';
            }

            if (!acc[key]) {
              acc[key] = [];
            }
            acc[key].push(rate);
            return acc;
          },
          {} as Record<string, ConsumableBuyAndSellRateType[]>
        )
      ).map(([key, rates]) => ({
        key,
        rates,
      }))
    : [];

export const groupedEquipmentBuyItems = (item: EquipmentCategoryItem) =>
  item?.buyRates
    ? Object.entries(
        item.buyRates.reduce(
          (
            acc: Record<string, EquipmentBuyAndSellRateType[]>,
            rate: EquipmentBuyAndSellRateType
          ) => {
            let key = '';
            if (rate?.branch && rate?.client) {
              key = `${rate.branch.name} / ${rate.client.legalName}`;
            } else if (rate?.branch) {
              key = rate.branch.name;
            } else {
              key = 'Default Price';
            }

            if (!acc[key]) {
              acc[key] = [];
            }
            acc[key].push(rate);
            return acc;
          },
          {} as Record<string, EquipmentBuyAndSellRateType[]>
        )
      ).map(([key, rates]) => ({
        key,
        rates,
      }))
    : [];

export const groupedEquipmentSellItems = (item: EquipmentCategoryItem) =>
  item?.sellRates
    ? Object.entries(
        item.sellRates.reduce(
          (
            acc: Record<string, EquipmentBuyAndSellRateType[]>,
            rate: EquipmentBuyAndSellRateType
          ) => {
            let key = '';
            if (rate?.branch && rate?.client) {
              key = `${rate.branch.name} / ${rate.client.legalName}`;
            } else if (rate?.branch) {
              key = rate.branch.name;
            } else if (rate?.client) {
              key = rate.client.legalName;
            } else {
              key = 'Default Price';
            }

            if (!acc[key]) {
              acc[key] = [];
            }
            acc[key].push(rate);
            return acc;
          },
          {} as Record<string, EquipmentBuyAndSellRateType[]>
        )
      ).map(([key, rates]) => ({
        key,
        rates,
      }))
    : [];

export const getConsumableDefaultFormValues = (
  editRateData?: ConsumableRateFormType
) => ({
  clientId: editRateData?.clientId || '',
  branchId: editRateData?.branchId || '',
  consumableItemId: editRateData?.consumableItemId || '',
  sellRate: editRateData?.sellRate || 0,
  buyRate: editRateData?.buyRate || 0,
  effectiveFrom: editRateData?.effectiveFrom?.slice(0, 10) || '',
  country: editRateData?.country || '',
  state: editRateData?.state || '',
  isDefault: editRateData?.isDefault || false,
});

export const getEquipmentDefaultFormValues = (
  editRateData?: EquipmentRateFormType
) => ({
  clientId: editRateData?.clientId || '',
  branchId: editRateData?.branchId || '',
  equipmentCategoryId: editRateData?.equipmentCategoryId || '',
  sellRate: editRateData?.sellRate || 0,
  buyRate: editRateData?.buyRate || 0,
  effectiveFrom: editRateData?.effectiveFrom?.slice(0, 10) || '',
  country: editRateData?.country || '',
  state: editRateData?.state || '',
  isDefault: editRateData?.isDefault || false,
});

export const getConsumableRateForBranch = (
  item: any,
  branchKey: string,
  priceType: 'buyRate' | 'sellRate'
) => {
  const rates =
    priceType === 'buyRate'
      ? item.buyRates || item.buyRates || []
      : item.sellRates || item.sellRates || [];

  // For buy rates, match branch name
  if (priceType === 'buyRate') {
    const rate = rates.find(
      (r: any) => getConsumableBuyBranchKey(r) === branchKey
    );
    return rate ? `${currencyType}${rate.buyRate}` : '-';
  }

  // For sell rates, match branch/client key
  if (priceType === 'sellRate') {
    const rate = rates.find(
      (r: any) => getConsumableSellBranchKey(r) === branchKey
    );
    return rate ? `${currencyType}${rate.sellRate}` : '-';
  }

  return '-';
};

export const getEquipmentRateForBranch = (
  item: any,
  branchKey: string,
  priceType: 'buyRate' | 'sellRate'
) => {
  const equipmentCategoryRates =
    priceType === 'buyRate'
      ? item.buyRates || item.buyRates || []
      : item.sellRates || item.sellRates || [];

  // For buy rates, match branch name
  if (priceType === 'buyRate') {
    const rate = equipmentCategoryRates.find(
      (r: any) => getEquipmentBuyBranchKey(r) === branchKey
    );
    return rate ? `${currencyType}${rate.buyRate}` : '-';
  }

  // For sell rates, match branch/client key
  if (priceType === 'sellRate') {
    const rate = equipmentCategoryRates.find(
      (r: any) => getEquipmentSellBranchKey(r) === branchKey
    );
    return rate ? `${currencyType}${rate.sellRate}` : '-';
  }

  return '-';
};

export const isSameDate = (date1: string, date2: string) => {
  const d1 = new Date(date1);
  const d2 = new Date(date2);
  return (
    d1.getFullYear() === d2.getFullYear() &&
    d1.getMonth() === d2.getMonth() &&
    d1.getDate() === d2.getDate()
  );
};

export const checkConsumableDuplication = (
  data: ConsumableRateFormType,
  consumableList: ConsumableItem[]
) => {
  const relatedConsumable = consumableList.find(
    (item) => item.id === data.consumableItemId
  );
  if (!consumableList || !relatedConsumable) {
    return { checkRateDuplicate: false, foundRateId: '' };
  }

  const isSellRate = Boolean(data.sellRate);
  const isBuyRate = Boolean(data.buyRate);

  if (isSellRate) {
    const identicalRate = relatedConsumable.sellRates?.find((rate) => {
      const isSameEffectiveDate =
        rate.effectiveFrom &&
        data.effectiveFrom &&
        isSameDate(rate.effectiveFrom, data.effectiveFrom);

      const isSameClient =
        rate.clientId === data.clientId || (!rate.clientId && !data.clientId);

      const isSameBranch =
        rate.branchId === data.branch?.id ||
        (!rate.branchId && !data.branch?.id);

      return (
        isSameEffectiveDate &&
        isSameClient &&
        isSameBranch &&
        data.isDefault === rate.isDefault
      );
    });

    if (identicalRate) {
      return { checkRateDuplicate: true, foundRateId: identicalRate.id };
    } else {
      return { checkRateDuplicate: false, foundRateId: '' };
    }
  } else if (isBuyRate) {
    const identicalRate = relatedConsumable.buyRates?.find((rate) => {
      const isSameEffectiveDate =
        rate.effectiveFrom &&
        data.effectiveFrom &&
        isSameDate(rate.effectiveFrom, data.effectiveFrom);

      const isSameBranch =
        rate.branchId === data.branch?.id ||
        (!rate.branchId && !data.branch?.id);

      return (
        isSameEffectiveDate && isSameBranch && data.isDefault === rate.isDefault
      );
    });

    if (identicalRate) {
      return { checkRateDuplicate: true, foundRateId: identicalRate.id };
    } else {
      return { checkRateDuplicate: false, foundRateId: '' };
    }
  }

  return { checkRateDuplicate: false, foundRateId: '' };
};

export const checkEquipmentDuplication = (
  data: EquipmentRateFormType,
  equipmentList: EquipmentCategoryItem[]
) => {
  const relatedEquipment = equipmentList.find(
    (item) => item.id === data.equipmentCategoryId
  );
  if (!equipmentList || !relatedEquipment) {
    return { checkRateDuplicate: false, foundRateId: '' };
  }

  const isSellRate = Boolean(data.sellRate);
  const isBuyRate = Boolean(data.buyRate);

  if (isSellRate) {
    const identicalRate = relatedEquipment.sellRates?.find((rate) => {
      const isSameEffectiveDate =
        rate.effectiveFrom &&
        data.effectiveFrom &&
        isSameDate(rate.effectiveFrom, data.effectiveFrom);

      const isSameClient =
        rate.clientId === data.clientId || (!rate.clientId && !data.clientId);

      const isSameBranch =
        rate.branchId === data.branch?.id ||
        (!rate.branchId && !data.branch?.id);

      return (
        isSameEffectiveDate &&
        isSameClient &&
        isSameBranch &&
        data.isDefault === rate.isDefault
      );
    });

    if (identicalRate) {
      return { checkRateDuplicate: true, foundRateId: identicalRate.id };
    } else {
      return { checkRateDuplicate: false, foundRateId: '' };
    }
  } else if (isBuyRate) {
    const identicalRate = relatedEquipment.buyRates?.find((rate) => {
      const isSameEffectiveDate =
        rate.effectiveFrom &&
        data.effectiveFrom &&
        isSameDate(rate.effectiveFrom, data.effectiveFrom);

      const isSameBranch =
        rate.branchId === data.branch?.id ||
        (!rate.branchId && !data.branch?.id);

      return (
        isSameEffectiveDate && isSameBranch && data.isDefault === rate.isDefault
      );
    });

    if (identicalRate) {
      return { checkRateDuplicate: true, foundRateId: identicalRate.id };
    } else {
      return { checkRateDuplicate: false, foundRateId: '' };
    }
  }

  return { checkRateDuplicate: false, foundRateId: '' };
};

export const checkLabourCostRateDuplication = (
  data: LabourCostRateFormType,
  labourRates: LabourCostRate[]
) => {
  const sameRoleRates = labourRates.filter((rate) => rate.role === data.role);
  const identicalRate = sameRoleRates.find((rate) => {
    const isSameEffectiveDate =
      rate.effectiveFrom &&
      data.effectiveFrom &&
      isSameDate(rate.effectiveFrom, data.effectiveFrom);

    return isSameEffectiveDate;
  });

  if (identicalRate) {
    return { checkRateDuplicate: true, foundRateId: identicalRate.id };
  } else {
    return { checkRateDuplicate: false, foundRateId: '' };
  }
};

export const checkLabourBillingRateDuplication = (
  data: LabourBillingRateFormType,
  labourRates: LabourBillingRate[]
) => {
  const sameRoleRates = labourRates.filter((rate) => rate.role === data.role);
  const identicalRate = sameRoleRates.find((rate) => {
    const isSameEffectiveDate =
      rate.effectiveFrom &&
      data.effectiveFrom &&
      isSameDate(rate.effectiveFrom, data.effectiveFrom);
    const isSameClient =
      rate.clientId === (data as LabourBillingRateFormType).clientId ||
      (!rate.clientId && !(data as LabourBillingRateFormType).clientId);
    const isSameBranch =
      rate.branchId === (data as LabourBillingRateFormType).branchId ||
      (!rate.branchId && !(data as LabourBillingRateFormType).branchId);

    return (
      isSameEffectiveDate &&
      isSameClient &&
      isSameBranch &&
      Boolean(data?.isDefault) === Boolean(rate.isDefault)
    );
  });

  console.log('Identical Rate Found:', identicalRate);

  if (identicalRate) {
    return { checkRateDuplicate: true, foundRateId: identicalRate.id };
  } else {
    return { checkRateDuplicate: false, foundRateId: '' };
  }
};

export const checkLabourOverrideCostRateDuplication = (
  data: LabourCostRateUserOverrideFormType,
  labourRates: LabourCostRateUserOverride[]
) => {
  const sameEmployeeRate = labourRates.filter(
    (rate) => rate.userId === data.userId
  );
  const identicalRate = sameEmployeeRate.find((rate) => {
    const isSameEffectiveDate =
      rate.effectiveFrom &&
      data.effectiveFrom &&
      isSameDate(rate.effectiveFrom, data.effectiveFrom);

    return isSameEffectiveDate;
  });

  if (identicalRate) {
    return { checkRateDuplicate: true, foundRateId: identicalRate.id };
  } else {
    return { checkRateDuplicate: false, foundRateId: '' };
  }
};

export const checkOtherExpenseRateDuplication = (
  data: OtherExpenseRateFormType,
  otherExpenseRates: OtherExpensesMargin[]
) => {
  // Custom categories share the CUSTOM type, so they differ by category id.
  const sameTypeRates = otherExpenseRates.filter(
    (rate) =>
      rate.otherExpenseType === data.otherExpenseType &&
      (rate.otherExpenseCategoryId ?? null) ===
        (data.otherExpenseCategoryId ?? null)
  );
  const identicalRate = sameTypeRates.find((rate) => {
    const isSameEffectiveDate =
      rate.effectiveFrom &&
      data.effectiveFrom &&
      isSameDate(rate.effectiveFrom, data.effectiveFrom);
    const isSameClient =
      rate.clientId === data.clientId || (!rate.clientId && !data.clientId);
    const isSameBranch =
      rate.branchId === (data as OtherExpenseRateFormType).branchId ||
      (!rate.branchId && !(data as OtherExpenseRateFormType).branchId);

    return (
      isSameEffectiveDate &&
      isSameClient &&
      isSameBranch &&
      Boolean(data?.isDefault) === Boolean(rate.isDefault)
    );
  });

  if (identicalRate) {
    return { checkRateDuplicate: true, foundRateId: identicalRate.id };
  } else {
    return { checkRateDuplicate: false, foundRateId: '' };
  }
};
